// src/components/ModifyVeilleModal.jsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { updateVeille } from '../utils/apiClient';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: bold;
  color: #333;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 150px;
  font-size: 1rem;
  resize: vertical;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  
  &.cancel {
    background-color: white;
    color: #4285f4;
    border: 1px solid #4285f4;
    
    &:hover {
      background-color: #f3f4f6;
    }
  }
  
  &.save {
    background-color: #4285f4;
    color: white;
    border: none;
    
    &:hover {
      background-color: #3b78e7;
    }
    
    &:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }
  }
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  background-color: ${props => props.type === 'error' ? '#f8d7da' : '#d4edda'};
  color: ${props => props.type === 'error' ? '#721c24' : '#155724'};
`;

const ModifyVeilleModal = ({ veille, onClose }) => {
  const [themes, setThemes] = useState(veille.themes);
  const [destinataire, setDestinataire] = useState(veille.destinataire);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!themes.trim()) {
      setAlert({ type: 'error', message: 'Veuillez saisir au moins un thème de veille.' });
      return;
    }
    
    if (!validateEmail(destinataire)) {
      setAlert({ type: 'error', message: 'Veuillez saisir une adresse email valide.' });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Mettre à jour via l'API PostgreSQL
      await updateVeille({
        created_at: veille.created_at,
        destinataire_original: veille.destinataire,
        themes,
        destinataire
      });
      
      setAlert({ type: 'success', message: 'Veille mise à jour avec succès!' });
      
      // Fermer le modal après un court délai
      setTimeout(() => {
        onClose(true); // true indique que des modifications ont été effectuées
      }, 1500);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la veille:', error);
      setAlert({ type: 'error', message: `Erreur: ${error.message}` });
      setIsSubmitting(false);
    }
  };
  
  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>Modifier la veille</ModalTitle>
          <CloseButton onClick={() => onClose(false)}>&times;</CloseButton>
        </ModalHeader>
        
        {alert && (
          <Alert type={alert.type}>
            {alert.message}
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="destinataire">Email du destinataire</Label>
            <Input
              id="destinataire"
              type="email"
              value={destinataire}
              onChange={(e) => setDestinataire(e.target.value)}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="themes">Thèmes de veille</Label>
            <Textarea
              id="themes"
              value={themes}
              onChange={(e) => setThemes(e.target.value)}
              required
            />
          </FormGroup>
          
          <ButtonGroup>
            <Button 
              type="button" 
              className="cancel" 
              onClick={() => onClose(false)}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="save"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </ButtonGroup>
        </Form>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ModifyVeilleModal;